import React, { useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import AdBanner from '../components/AdBanner';
import './LandingPage.css';

// SEO-optimized landing page configurations
const landingPageConfig = {
  'car': {
    title: 'Free Car Bill of Sale',
    metaTitle: 'Free Car Bill of Sale Template | Download PDF | PrivateSold',
    metaDescription: 'Create a free car bill of sale in minutes. Professional PDF template for private vehicle sales on Craigslist, Facebook Marketplace. No signup required.',
    h1: 'Free Car Bill of Sale Generator',
    subtitle: 'Create a professional bill of sale for your car, truck, or SUV purchase',
    icon: '🚗',
    keywords: ['car', 'auto', 'automobile', 'sedan', 'SUV', 'truck', 'pickup'],
    templateType: 'vehicle',
    features: [
      'VIN number documentation',
      'Odometer disclosure',
      'Title transfer information',
      'As-is or warranty terms'
    ],
    faqs: [
      {
        q: 'Do I need a bill of sale to sell a car?',
        a: 'While requirements vary by state, a bill of sale documents the transaction, helps protect both buyer and seller, and may be required for title transfer and registration. Check your local requirements.'
      },
      {
        q: 'What information should be on a car bill of sale?',
        a: 'A car bill of sale should include: buyer and seller names and addresses, vehicle details (year, make, model, VIN), odometer reading, sale price, date of sale, and signatures from both parties.'
      },
      {
        q: 'What does a bill of sale prove?',
        a: 'A signed bill of sale serves as a written record of the transaction and can help prove ownership transfer. Requirements vary by state—check your local laws.'
      }
    ]
  },
  'motorcycle': {
    title: 'Free Motorcycle Bill of Sale',
    metaTitle: 'Free Motorcycle Bill of Sale Template | Download PDF | PrivateSold',
    metaDescription: 'Generate a free motorcycle bill of sale instantly. Perfect for private sales of motorcycles, scooters, and ATVs. Download PDF immediately.',
    h1: 'Free Motorcycle Bill of Sale Generator',
    subtitle: 'Professional bill of sale for motorcycles, scooters, ATVs, and dirt bikes',
    icon: '🏍️',
    keywords: ['motorcycle', 'bike', 'scooter', 'ATV', 'dirt bike', 'moped'],
    templateType: 'vehicle',
    features: [
      'VIN verification fields',
      'Mileage documentation',
      'Title status disclosure',
      'Safety inspection notes'
    ],
    faqs: [
      {
        q: 'What do I need to sell a motorcycle privately?',
        a: 'You need the motorcycle title, a bill of sale, and valid ID. Some states also require a smog or safety inspection certificate.'
      },
      {
        q: 'Does a motorcycle bill of sale need to be notarized?',
        a: 'Most states do not require notarization for a motorcycle bill of sale, but some do. Check your state\'s DMV requirements.'
      }
    ]
  },
  'boat': {
    title: 'Free Boat Bill of Sale',
    metaTitle: 'Free Boat Bill of Sale Template | Watercraft PDF | PrivateSold',
    metaDescription: 'Create a free boat bill of sale for private watercraft sales. Includes hull ID, motor details, and trailer information. Download PDF instantly.',
    h1: 'Free Boat Bill of Sale Generator',
    subtitle: 'Complete bill of sale for boats, jet skis, and watercraft',
    icon: '🚤',
    keywords: ['boat', 'watercraft', 'jet ski', 'yacht', 'sailboat', 'kayak'],
    templateType: 'vehicle',
    features: [
      'Hull identification number',
      'Motor/engine specifications',
      'Trailer information',
      'Coast Guard documentation'
    ],
    faqs: [
      {
        q: 'What information is required on a boat bill of sale?',
        a: 'Include the hull identification number (HIN), boat make/model/year, motor details, trailer info if included, sale price, and both parties\' information.'
      }
    ]
  },
  'phone': {
    title: 'Free Phone Bill of Sale',
    metaTitle: 'Free Cell Phone Bill of Sale | iPhone, Android | PrivateSold',
    metaDescription: 'Generate a free bill of sale for selling phones. Perfect for iPhone, Samsung, and Android sales on Facebook Marketplace or Craigslist.',
    h1: 'Free Cell Phone Bill of Sale Generator',
    subtitle: 'Professional receipt for iPhone, Samsung, and smartphone sales',
    icon: '📱',
    keywords: ['phone', 'iPhone', 'Samsung', 'Android', 'smartphone', 'cell phone'],
    templateType: 'electronics',
    features: [
      'IMEI number documentation',
      'Storage capacity details',
      'Carrier unlock status',
      'Condition assessment'
    ],
    faqs: [
      {
        q: 'Why do I need a bill of sale for a phone?',
        a: 'A bill of sale protects you if the phone is later reported stolen or if there\'s a dispute about the sale. It proves you purchased the device legitimately.'
      },
      {
        q: 'Should I include the IMEI on the bill of sale?',
        a: 'Yes, including the IMEI helps verify the phone\'s identity and proves which specific device was sold in case of disputes.'
      }
    ]
  },
  'laptop': {
    title: 'Free Laptop Bill of Sale',
    metaTitle: 'Free Laptop Bill of Sale Template | MacBook, PC | PrivateSold',
    metaDescription: 'Create a bill of sale for laptop sales. Perfect for MacBook, Dell, HP, and other computer sales. Download free PDF template.',
    h1: 'Free Laptop Bill of Sale Generator',
    subtitle: 'Bill of sale for MacBooks, Windows laptops, and computers',
    icon: '💻',
    keywords: ['laptop', 'MacBook', 'computer', 'PC', 'notebook', 'Dell', 'HP'],
    templateType: 'electronics',
    features: [
      'Serial number tracking',
      'Specifications documentation',
      'Software/license info',
      'Accessory list'
    ],
    faqs: [
      {
        q: 'What should I include when selling a laptop?',
        a: 'Include the serial number, make/model, storage capacity, RAM, condition, any included accessories (charger, case), and whether software licenses transfer.'
      }
    ]
  },
  'furniture': {
    title: 'Free Furniture Bill of Sale',
    metaTitle: 'Free Furniture Bill of Sale Template | Download PDF | PrivateSold',
    metaDescription: 'Generate a free bill of sale for furniture sales. Perfect for couches, tables, beds, and home goods sold on Craigslist or Facebook Marketplace.',
    h1: 'Free Furniture Bill of Sale Generator',
    subtitle: 'Professional receipt for furniture and home goods transactions',
    icon: '🛋️',
    keywords: ['furniture', 'couch', 'sofa', 'table', 'bed', 'dresser', 'desk'],
    templateType: 'equipment',
    features: [
      'Detailed item description',
      'Condition documentation',
      'Dimensions and materials',
      'Delivery arrangements'
    ],
    faqs: [
      {
        q: 'Do I need a bill of sale for furniture?',
        a: 'While not legally required, a bill of sale protects both parties and documents the transaction details, especially for expensive items.'
      }
    ]
  },
  'trailer': {
    title: 'Free Trailer Bill of Sale',
    metaTitle: 'Free Trailer Bill of Sale Template | Utility, Cargo | PrivateSold',
    metaDescription: 'Create a free trailer bill of sale. Perfect for utility trailers, cargo trailers, and RVs. Includes VIN and registration info.',
    h1: 'Free Trailer Bill of Sale Generator',
    subtitle: 'Bill of sale for utility trailers, cargo trailers, and RVs',
    icon: '🚛',
    keywords: ['trailer', 'utility trailer', 'cargo trailer', 'RV', 'camper', 'travel trailer'],
    templateType: 'vehicle',
    features: [
      'VIN documentation',
      'Weight and dimensions',
      'Registration transfer',
      'Hitch specifications'
    ],
    faqs: [
      {
        q: 'Do trailers need a bill of sale?',
        a: 'Yes, most states require a bill of sale for trailer registration and title transfer, especially for trailers over a certain weight.'
      }
    ]
  },
  'golf-cart': {
    title: 'Free Golf Cart Bill of Sale',
    metaTitle: 'Free Golf Cart Bill of Sale Template | EZ-GO, Club Car | PrivateSold',
    metaDescription: 'Generate a free golf cart bill of sale. Perfect for EZ-GO, Club Car, and Yamaha cart sales. Download PDF instantly.',
    h1: 'Free Golf Cart Bill of Sale Generator',
    subtitle: 'Professional bill of sale for golf carts and utility vehicles',
    icon: '🏌️',
    keywords: ['golf cart', 'EZ-GO', 'Club Car', 'Yamaha', 'utility vehicle'],
    templateType: 'vehicle',
    features: [
      'Serial number tracking',
      'Battery condition',
      'Electric vs gas details',
      'Accessory documentation'
    ],
    faqs: [
      {
        q: 'Do golf carts have titles?',
        a: 'It depends on your state. Some states title golf carts like vehicles, while others only require registration for street-legal carts.'
      }
    ]
  }
};

const LandingPage = () => {
  const { type } = useParams();
  const config = landingPageConfig[type];

  // Update meta tags for SEO
  useEffect(() => {
    if (!config) return;
    
    document.title = config.metaTitle;
    
    // Update meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', config.metaDescription);
    }

    // Cleanup
    return () => {
      document.title = 'PrivateSold - Free Bill of Sale & Invoice Generator';
    };
  }, [config]);

  // Redirect to main bill of sale page if invalid type
  if (!config) {
    return <Navigate to="/bill-of-sale" replace />;
  }

  return (
    <div className="landing-page">
      <div className="container">
        {/* Hero Section */}
        <section className="landing-hero">
          <div className="landing-hero-content">
            <span className="landing-icon">{config.icon}</span>
            <h1 className="landing-title">{config.h1}</h1>
            <p className="landing-subtitle">{config.subtitle}</p>
            <div className="landing-cta-buttons">
              <Link 
                to={`/bill-of-sale?template=${config.templateType}`} 
                className="btn btn-primary btn-lg"
              >
                Create {config.title} Now
              </Link>
              <span className="landing-free-badge">✓ 100% Free</span>
            </div>
          </div>
        </section>

        <AdBanner slot="landing-top" />

        {/* Features Section */}
        <section className="landing-section">
          <h2>What's Included in Your {config.title}</h2>
          <div className="landing-features">
            {config.features.map((feature, index) => (
              <div key={index} className="landing-feature">
                <span className="feature-check">✓</span>
                <span>{feature}</span>
              </div>
            ))}
            <div className="landing-feature">
              <span className="feature-check">✓</span>
              <span>Instant PDF download</span>
            </div>
            <div className="landing-feature">
              <span className="feature-check">✓</span>
              <span>No signup required</span>
            </div>
            <div className="landing-feature">
              <span className="feature-check">✓</span>
              <span>Professional documentation</span>
            </div>
            <div className="landing-feature">
              <span className="feature-check">✓</span>
              <span>Works in all 50 states</span>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="landing-section">
          <h2>How It Works</h2>
          <div className="landing-steps">
            <div className="landing-step">
              <div className="step-number">1</div>
              <h3>Fill Out the Form</h3>
              <p>Enter seller and buyer information, {type} details, and sale terms</p>
            </div>
            <div className="landing-step">
              <div className="step-number">2</div>
              <h3>Add Signatures</h3>
              <p>Both parties can sign digitally right in the browser</p>
            </div>
            <div className="landing-step">
              <div className="step-number">3</div>
              <h3>Download PDF</h3>
              <p>Get your professional bill of sale instantly as a PDF</p>
            </div>
          </div>
        </section>

        <AdBanner slot="landing-middle" />

        {/* FAQs */}
        {config.faqs && config.faqs.length > 0 && (
          <section className="landing-section">
            <h2>Frequently Asked Questions</h2>
            <div className="landing-faqs">
              {config.faqs.map((faq, index) => (
                <div key={index} className="landing-faq">
                  <h3>{faq.q}</h3>
                  <p>{faq.a}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Final CTA */}
        <section className="landing-section landing-final-cta">
          <h2>Ready to Create Your {config.title}?</h2>
          <p>Join thousands of users who trust PrivateSold for their private sale documentation.</p>
          <Link 
            to={`/bill-of-sale?template=${config.templateType}`} 
            className="btn btn-primary btn-lg"
          >
            Create Free {config.title}
          </Link>
        </section>

        {/* Related Pages */}
        <section className="landing-section">
          <h2>Other Bill of Sale Templates</h2>
          <div className="landing-related">
            {Object.entries(landingPageConfig)
              .filter(([key]) => key !== type)
              .slice(0, 4)
              .map(([key, value]) => (
                <Link key={key} to={`/bill-of-sale/${key}`} className="related-link">
                  <span className="related-icon">{value.icon}</span>
                  <span>{value.title}</span>
                </Link>
              ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default LandingPage;
